import React from "react";
import { motion } from "framer-motion";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "./ui/accordion";

const faqs = [
  {
    question: "When should we start the admissions process?",
    answer:
      "Ideally, 18–24 months before your target entry year. For competitive schools and scholarship applicants, earlier engagement allows time for thorough preparation. However, we also support families on tighter timelines where possible.",
  },
  {
    question: "Which ages do you support?",
    answer:
      "We work with students aged 11–18, covering entry points at Year 7, Year 9, Year 10, Year 12 (Sixth Form), and other transition years depending on the school system.",
  },
  {
    question: "Do you help beyond the UK?",
    answer:
      "Yes. While UK boarding schools are our core focus, we also advise on schools in Switzerland, the United States, and other leading destinations where our network extends.",
  },
  {
    question: "How do you shortlist schools?",
    answer:
      "We assess academic fit, co-curricular interests, pastoral environment, location preferences, and family values. Our shortlist is strategic — not a list of 'famous' schools, but schools where your child will genuinely thrive.",
  },
  {
    question: "What if my child is not ready?",
    answer:
      "We'll tell you. If your child needs more time to develop academically or emotionally, we will recommend waiting or suggest preparatory steps. Honest assessment is central to our approach.",
  },
  {
    question: "Do you help with scholarships?",
    answer:
      "Absolutely. We identify scholarship opportunities matched to your child's strengths — academic, music, art, drama, sport — and guide you through the application and audition process.",
  },
  {
    question: "How many schools should we apply to?",
    answer:
      "Typically 4–6 schools, including a strategic mix of aspirational, target, and secure options. We help you build a balanced list that maximises opportunity without overwhelming your child.",
  },
  {
    question: "Do you support interviews and tests?",
    answer:
      "Yes. We provide comprehensive preparation for UKiset, ISEB Pre-test, CAT4, and school-specific assessments. For interviews, we offer one-to-one coaching focused on authentic self-presentation.",
  },
  {
    question: "Can you support school visits?",
    answer:
      "We help plan and optimise your visit schedule, advise on what to look for, and provide questions to ask admissions teams. Some families also request us to accompany them.",
  },
  {
    question: "What does the strategy call include?",
    answer:
      "During our initial 30-minute call, we discuss your child's profile, your family's goals, your timeline, and any concerns. We'll also outline how ESA can support you and answer any questions you have.",
  },
];

const FAQ = () => {
  return (
    <section id="faq" className="py-20 md:py-28 bg-[#FDFCF8]">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="max-w-3xl mx-auto text-center mb-12"
        >
          <p className="text-sm uppercase tracking-[0.2em] font-semibold text-[#C9A227] mb-4">
            Questions & Answers
          </p>
          <h2
            className="text-3xl md:text-4xl lg:text-5xl font-medium text-[#1A202C] tracking-tight mb-6"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Frequently Asked <span className="text-[#2B4C7E]">Questions</span>
          </h2>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="max-w-3xl mx-auto"
        >
          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem
                key={index}
                value={`item-${index}`}
                className="faq-item bg-white border border-[#E2E8F0] rounded-[4px] px-6 data-[state=open]:border-[#C9A227]"
                data-testid={`faq-item-${index}`}
              >
                <AccordionTrigger
                  className="text-left py-5 hover:no-underline"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  <span className="text-base md:text-lg font-medium text-[#1A202C] pr-4">
                    {faq.question}
                  </span>
                </AccordionTrigger>
                <AccordionContent className="pb-5 text-[#4A5568] leading-relaxed">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </motion.div>
      </div>
    </section>
  );
};

export default FAQ;
